// AufrufControlPage.jsx — Schreiber-Steuerung für Kämpfer-Aufruf
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import './AufrufControlPage.css';

function getSlotStatusSimple(slot, turnierdatum) {
  if (!turnierdatum || !slot.start_time) return 'upcoming';
  const datePart = String(turnierdatum).slice(0, 10);
  const now = Date.now();
  const start = new Date(`${datePart}T${slot.start_time}:00`);
  if (now < start.getTime()) return 'upcoming';
  if (slot.end_time) {
    const end = new Date(`${datePart}T${slot.end_time}:00`);
    return now < end.getTime() ? 'active' : 'done';
  }
  return now < start.getTime() + 3600000 ? 'active' : 'done';
}

export default function AufrufControlPage() {
  const { turnierId } = useParams();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [activeCall, setActiveCall] = useState(null); // aktuell aufgerufene Kategorie
  const [calling, setCalling]   = useState(null);     // welcher Button lädt gerade
  const [sperre, setSperre]     = useState('');       // Hinweis, wenn die Anmeldung fehlt

  const load = useCallback(() => {
    fetch(`/api/brackets/turnier/${turnierId}/aufruf`)
      .then(r => r.json())
      .then(d => { if (d.success) setData(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [turnierId]);

  const loadState = useCallback(() => {
    fetch(`/api/brackets/turnier/${turnierId}/aufruf-state`)
      .then(r => r.json())
      .then(d => setActiveCall(d.state || null))
      .catch(() => {});
  }, [turnierId]);

  useEffect(() => {
    load();
    loadState();
    const i1 = setInterval(load, 15000);
    const i2 = setInterval(loadState, 3000);
    return () => { clearInterval(i1); clearInterval(i2); };
  }, [load, loadState]);

  // Die Steuerung verlangt seit 26.08.2026 eine Anmeldung als Turnier-Personal
  // (Anmeldung, Waage, Kampfrichter) oder als Verband. Der Token liegt nach dem
  // Login unter `token` — beide Personal-Anmeldungen legen ihn dort ab.
  //
  // Fehlt er oder passt er nicht zum Turnier, wird das angezeigt statt still
  // zu scheitern: am Wettkampftag muss sofort erkennbar sein, warum ein Aufruf
  // nicht auf der Matte ankommt.
  async function senden(nutzlast) {
    const token = localStorage.getItem('token');
    if (!token) {
      setSperre('Nicht angemeldet — bitte über /reg-login oder /staff-login anmelden.');
      return false;
    }
    try {
      const res = await fetch(`/api/brackets/turnier/${turnierId}/aufruf-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(nutzlast),
      });
      if (res.status === 401) { setSperre('Anmeldung abgelaufen — bitte neu anmelden.'); return false; }
      if (res.status === 403) { setSperre('Dieser Zugang gehört nicht zu diesem Turnier.'); return false; }
      if (!res.ok)            { setSperre('Der Aufruf konnte nicht gesendet werden.'); return false; }
      setSperre('');
      return true;
    } catch {
      setSperre('Keine Verbindung — der Aufruf kam nicht an.');
      return false;
    }
  }

  async function call(division_code, label, tatami) {
    setCalling(division_code);
    const ok = await senden({ division_code, label, tatami });
    if (ok) setActiveCall({ division_code, label, tatami });
    setCalling(null);
  }

  async function clearCall() {
    if (await senden({ division_code: null })) setActiveCall(null);
  }

  const turnierdatum = data?.turnier?.datum ?? null;

  // Alle aufzurufenden Elemente: ready Kämpfe + zeitplan Slots
  const items = (() => {
    if (!data) return [];
    const list = [];

    // Bereit-Kämpfe
    for (const k of data.ready) {
      list.push({
        type: 'kampf',
        division_code: k.division_code || k.bracket_name,
        label: k.bracket_name,
        tatami: k.tatami_nummer ?? k.matte_nummer,
        time: null,
        detail: `Runde ${k.runde} — ${k.k1_vorname ?? '?'} ${k.k1_nachname ?? ''} vs ${k.k2_vorname ?? '?'} ${k.k2_nachname ?? ''}`,
        sortKey: `${String(k.tatami_nummer ?? k.matte_nummer ?? 99).padStart(3,'0')}_0_${k.bracket_name}`,
        urgent: true,
      });
    }

    // Pending Kämpfe (nur die ersten pro Tatami)
    const seenPending = new Set();
    for (const k of data.pending) {
      const tatami = k.tatami_nummer ?? k.matte_nummer;
      const key = `${tatami}_${k.bracket_id}`;
      if (seenPending.has(key)) continue;
      seenPending.add(key);
      list.push({
        type: 'kampf',
        division_code: k.division_code || k.bracket_name,
        label: k.bracket_name,
        tatami,
        time: k.geplante_zeit,
        detail: `Nächster: ${k.k1_vorname ?? '?'} ${k.k1_nachname ?? ''} vs ${k.k2_vorname ?? '?'} ${k.k2_nachname ?? ''}`,
        sortKey: `${String(tatami ?? 99).padStart(3,'0')}_1_${k.bracket_name}`,
        urgent: false,
      });
    }

    // Zeitplan-Slots
    for (const s of (data.zeitplan ?? [])) {
      const status = getSlotStatusSimple(s, turnierdatum);
      if (status === 'done') continue;
      list.push({
        type: 'slot',
        division_code: s.division_code,
        label: s.division_code,
        tatami: s.ring,
        time: s.start_time,
        detail: s.end_time ? `${s.start_time}–${s.end_time} Uhr` : s.start_time ? `${s.start_time} Uhr` : '',
        sortKey: `${String(s.ring ?? 99).padStart(3,'0')}_${s.start_time ?? '99:99'}`,
        status,
        urgent: status === 'active',
      });
    }

    // Deduplizieren nach division_code, sortieren
    const seen = new Set();
    return list
      .filter(it => { if (seen.has(it.division_code)) return false; seen.add(it.division_code); return true; })
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  })();

  // Gruppieren nach Tatami
  const byTatami = {};
  for (const it of items) {
    const key = it.tatami ?? 0;
    if (!byTatami[key]) byTatami[key] = [];
    byTatami[key].push(it);
  }
  const tatamiNrs = Object.keys(byTatami).map(Number).sort((a, b) => a - b);

  if (loading) return (
    <div className="acp-page">
      <div className="acp-loading">Lade Turnierdaten…</div>
    </div>
  );

  return (
    <div className="acp-page">
      <header className="acp-header">
        <div className="acp-header-title">Schreiber-Steuerung</div>
        {data?.turnier && <div className="acp-header-sub">{data.turnier.name}</div>}
      </header>

      {sperre && (
        <div className="acp-sperre" role="alert">
          {sperre}
        </div>
      )}

      {activeCall && (
        <div className="acp-active-bar">
          <div className="acp-active-label">
            <span className="acp-active-dot" />
            Aufgerufen: <strong>{activeCall.label || activeCall.division_code}</strong>
            {activeCall.tatami ? ` · Tatami ${activeCall.tatami}` : ''}
          </div>
          <button className="acp-clear-btn" onClick={clearCall}>Aufruf beenden ✕</button>
        </div>
      )}

      {items.length === 0 ? (
        <div className="acp-empty">Keine aufzurufenden Kategorien vorhanden.</div>
      ) : (
        <div className="acp-content">
          {tatamiNrs.map(nr => (
            <div key={nr} className="acp-tatami-group">
              <div className="acp-tatami-title">Tatami {nr || '—'}</div>
              <div className="acp-items">
                {byTatami[nr].map(it => {
                  const isActive = activeCall?.division_code === it.division_code;
                  const isLoading = calling === it.division_code;
                  return (
                    <div key={it.division_code} className={`acp-item${it.urgent ? ' acp-item--urgent' : ''}${isActive ? ' acp-item--active' : ''}`}>
                      <div className="acp-item-info">
                        <div className="acp-item-label">{it.label}</div>
                        {it.detail && <div className="acp-item-detail">{it.detail}</div>}
                      </div>
                      <button
                        className={`acp-call-btn${isActive ? ' acp-call-btn--active' : ''}`}
                        onClick={() => isActive ? clearCall() : call(it.division_code, it.label, it.tatami)}
                        disabled={isLoading}
                      >
                        {isLoading ? '…' : isActive ? '✓ Aktiv' : 'Aufrufen'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
